// src/pages/drone/buah-alami/components/MapActionTooltip.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
    Tooltip,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Alert,
    CircularProgress,
    Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MapIcon from '@mui/icons-material/Map';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { ref, get, update } from 'firebase/database';

const MapActionTooltip = ({ 
    database, 
    id_lokasi, 
    lokasiNama, 
    firebaseKey,
    onDataUpdated,
    children // Prop baru untuk children
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openViewer, setOpenViewer] = useState(false);
    const [mapLink, setMapLink] = useState('');
    const [existingMapLink, setExistingMapLink] = useState('');
    const [actionType, setActionType] = useState(''); // 'add' or 'edit'
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    // Ref untuk dialog
    const dialogRef = useRef(null);

    // Cek apakah sudah ada peta
    useEffect(() => {
        const checkExistingMap = async () => {
            if (!firebaseKey || !id_lokasi) return;
            
            try {
                const mapRef = ref(database, `db_drone/tb_buah_alami/${firebaseKey}`);
                const snapshot = await get(mapRef);
                
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    setExistingMapLink(data.map_pdf_link || '');
                }
            } catch (error) {
                console.error('Error checking existing map:', error);
            }
        };
        
        checkExistingMap();
    }, [database, firebaseKey, id_lokasi]);

    const handleTooltipClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleAddMap = () => {
        setActionType('add');
        setMapLink('');
        setOpenDialog(true);
        handleMenuClose();
    };

    const handleEditMap = () => {
        setActionType('edit');
        setMapLink(existingMapLink);
        setOpenDialog(true);
        handleMenuClose();
    };

    const handleViewMap = () => {
        if (existingMapLink) {
            setOpenViewer(true);
        } else {
            setSnackbar({
                open: true,
                message: 'Tidak ada peta yang tersimpan',
                severity: 'warning'
            });
        }
        handleMenuClose();
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setMapLink('');
    };

    const handleViewerClose = () => {
        setOpenViewer(false);
    };

    const validateUrl = (url) => {
        if (!url) return false;
        try {
            // Validasi URL
            const parsedUrl = new URL(url);
            return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
        } catch {
            return false;
        }
    };

    const saveMapLink = async () => {
        if (!mapLink.trim()) {
            setSnackbar({
                open: true,
                message: 'Link peta tidak boleh kosong',
                severity: 'warning'
            });
            return;
        }

        if (!validateUrl(mapLink)) {
            setSnackbar({
                open: true,
                message: 'Format link tidak valid. Harus berupa URL lengkap (http:// atau https://)',
                severity: 'warning'
            });
            return;
        }

        setLoading(true);
        try {
            const timestamp = new Date().toISOString();
            
            if (actionType === 'add') {
                // Tambah data baru
                const newData = {
                    map_pdf_link: mapLink.trim(),
                    map_updated_at: timestamp,
                    map_updated_by: 'user' // Ganti dengan user yang sedang login jika ada
                };
                
                await update(ref(database, `db_drone/tb_buah_alami/${firebaseKey}`), newData);
                setExistingMapLink(mapLink.trim());
                
                setSnackbar({
                    open: true,
                    message: 'Peta berhasil ditambahkan',
                    severity: 'success'
                });
            } else if (actionType === 'edit') {
                // Edit data existing
                const updateData = {
                    map_pdf_link: mapLink.trim(),
                    map_updated_at: timestamp,
                    map_updated_by: 'user' // Ganti dengan user yang sedang login jika ada
                };
                
                await update(ref(database, `db_drone/tb_buah_alami/${firebaseKey}`), updateData);
                setExistingMapLink(mapLink.trim());
                
                setSnackbar({
                    open: true,
                    message: 'Peta berhasil diperbarui',
                    severity: 'success'
                });
            }
            
            // Trigger callback untuk refresh data jika diperlukan
            if (onDataUpdated) {
                onDataUpdated();
            }
            
            setOpenDialog(false);
            setMapLink('');
            
        } catch (error) {
            console.error('Error saving map link:', error);
            setSnackbar({
                open: true,
                message: 'Gagal menyimpan peta: ' + error.message,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const hasMap = !!existingMapLink;

    return (
        <>
            {/* Tooltip dengan children - MODIFIKASI DI SINI */}
            <Tooltip 
                title="Pilih Aksi" 
                arrow
                placement="top"
            >
                <Box
                    onClick={handleTooltipClick}
                    sx={{ 
                        cursor: 'pointer',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}
                >
                    {children}
                </Box>
            </Tooltip>

            {/* Menu Aksi */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                {!hasMap ? (
                    <MenuItem onClick={handleAddMap}>
                        <ListItemIcon>
                            <AddIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Tambah Peta</ListItemText>
                    </MenuItem>
                ) : (
                    <>
                        <MenuItem onClick={handleEditMap}>
                            <ListItemIcon>
                                <EditIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Edit Peta</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleViewMap}>
                            <ListItemIcon>
                                <VisibilityIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Lihat Peta</ListItemText>
                        </MenuItem>
                    </>
                )}
            </Menu>

            {/* Dialog Input Link Peta */}
            <Dialog
                open={openDialog}
                onClose={handleDialogClose}
                maxWidth="md"
                fullWidth
                ref={dialogRef}
            >
                <DialogTitle>
                    {actionType === 'add' ? 'Tambah Peta PDF' : 'Edit Peta PDF'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Masukkan link PDF peta untuk lokasi: <strong>{lokasiNama}</strong>
                        </Alert>
                        
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Link Peta PDF"
                            type="url"
                            fullWidth
                            variant="outlined"
                            value={mapLink}
                            onChange={(e) => setMapLink(e.target.value)}
                            placeholder="https://example.com/peta.pdf"
                            helperText="Masukkan URL lengkap yang mengarah ke file PDF"
                            error={mapLink && !validateUrl(mapLink)}
                        />
                        
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PictureAsPdfIcon color="error" />
                            <span style={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                                Pastikan link mengarah ke file PDF yang dapat diakses
                            </span>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>Batal</Button>
                    <Button 
                        onClick={saveMapLink} 
                        variant="contained"
                        disabled={loading || !mapLink.trim()}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? 'Menyimpan...' : actionType === 'add' ? 'Simpan Peta' : 'Update Peta'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog View PDF */}
            <Dialog
                open={openViewer}
                onClose={handleViewerClose}
                maxWidth="xl"
                fullWidth
                fullScreen={true}
            >
                <DialogTitle>
                    Peta PDF - {lokasiNama}
                    <IconButton
                        aria-label="close"
                        onClick={handleViewerClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <VisibilityIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, height: 'calc(100vh - 64px)' }}>
                    {existingMapLink && (
                        <iframe
                            src={existingMapLink}
                            title={`Peta ${lokasiNama}`}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default MapActionTooltip;